import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Popover from '@mui/material/Popover'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { DateRangePicker } from 'react-date-range'
import { subDays, format, parseISO } from 'date-fns'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import { filterButtonSx } from '../../common/sharedStyles'
import { dayStr, quickRanges } from './freightPnlUtils'

const MAX_RANGE_DAYS = 92

/**
 * filters = { segment: 'D2C'|'PTL', from: 'yyyy-MM-dd', to: 'yyyy-MM-dd', merchantOids: [], lossMakingOnly: bool, lossThresholdPct: number }
 * Mirrors the four Control Tower mails: D2C/PTL daily = segment + one day; rolling weekly = 7-day range;
 * loss-making clients = lossMakingOnly with the 10 % threshold.
 */
export default function FreightPnlFilters({ filters, onChange, disabled }) {
  const { data: merchantsRaw = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchants = merchantsRaw
    .filter(m => m.is_deleted === '0')
    .map(m => ({ label: m.companyName ? `${m.name} (${m.companyName})` : m.name, value: m.id }))

  const [anchorEl, setAnchorEl] = useState(null)
  const [range, setRange] = useState([{ startDate: parseISO(filters.from), endDate: parseISO(filters.to), key: 'selection' }])

  const openDates = (e) => {
    setRange([{ startDate: parseISO(filters.from), endDate: parseISO(filters.to), key: 'selection' }])
    setAnchorEl(e.currentTarget)
  }
  const applyDates = () => {
    setAnchorEl(null)
    onChange({ ...filters, from: dayStr(range[0].startDate), to: dayStr(range[0].endDate) })
  }

  const staticRanges = quickRanges().map(r => ({
    label: r.label,
    range: () => ({ startDate: r.from, endDate: r.to }),
    isSelected: (sel) => dayStr(sel.startDate) === dayStr(r.from) && dayStr(sel.endDate) === dayStr(r.to),
  }))

  const dateLabel = filters.from === filters.to
    ? format(parseISO(filters.from), 'dd MMM yyyy')
    : `${format(parseISO(filters.from), 'dd MMM')} – ${format(parseISO(filters.to), 'dd MMM yyyy')}`

  return (
    <Paper variant='outlined' sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
      <ToggleButtonGroup
        size='small' exclusive value={filters.segment}
        onChange={(_, v) => v && onChange({ ...filters, segment: v })}
        disabled={disabled}
      >
        <ToggleButton value='D2C' sx={{ px: 2 }}>D2C</ToggleButton>
        <ToggleButton value='PTL' sx={{ px: 2 }}>PTL</ToggleButton>
      </ToggleButtonGroup>

      <Button variant='outlined' size='small' startIcon={<CalendarMonthOutlinedIcon />} onClick={openDates} sx={{ textTransform: 'none', fontWeight: 600 }} disabled={disabled}>
        {dateLabel}
      </Button>
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1 }}>
          <DateRangePicker
            ranges={range}
            onChange={(item) => setRange([item.selection])}
            maxDate={new Date()}
            minDate={subDays(new Date(), MAX_RANGE_DAYS)}
            staticRanges={staticRanges}
            inputRanges={[]}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
            <Button size='small' sx={filterButtonSx('reset')} onClick={() => setAnchorEl(null)}>Cancel</Button>
            <Button size='small' variant='contained' sx={filterButtonSx('apply')} onClick={applyDates}>Apply</Button>
          </Box>
        </Box>
      </Popover>

      <Autocomplete
        multiple size='small' limitTags={2} disableCloseOnSelect
        options={merchants}
        value={merchants.filter(m => (filters.merchantOids || []).includes(m.value))}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onChange={(_, sel) => onChange({ ...filters, merchantOids: sel.map(m => m.value) })}
        renderInput={(params) => <TextField {...params} placeholder='All merchants' />}
        sx={{ minWidth: 280 }}
        disabled={disabled}
      />

      <FormControlLabel
        sx={{ ml: 1 }}
        control={<Switch size='small' checked={Boolean(filters.lossMakingOnly)} onChange={(e) => onChange({ ...filters, lossMakingOnly: e.target.checked })} disabled={disabled} />}
        label={<Box component='span' sx={{ fontSize: 13, fontWeight: 600 }}>Loss-making brands only</Box>}
      />
      <TextField
        size='small' type='number' label='Below %' value={filters.lossThresholdPct}
        onChange={(e) => onChange({ ...filters, lossThresholdPct: e.target.value === '' ? '' : Number(e.target.value) })}
        inputProps={{ min: -100, max: 100, step: 1 }}
        sx={{ width: 96 }}
        disabled={disabled || !filters.lossMakingOnly}
      />
    </Paper>
  )
}
